import { createContext, useContext, useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { getToken, getUser } from "../../lib/auth";
import { getCurrentUser } from "../../lib/api";
import type { PermissionsContextType, PermissionsProviderProps } from "./permissions.types";

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

// Initializes from the existing gg-hub session (gg-hub.session in localStorage),
// so there's no need to store permissions in a separate key — they already live
// inside the session object written by saveSession() during login.
export const PermissionsProvider = ({ children }: PermissionsProviderProps) => {
  const [permissions, setPermissions] = useState<string[]>(
    () => getUser()?.permissions ?? []
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    // Busca permissões ATUALIZADAS do backend (rota /auth/me) no mount.
    // Permissões são adicionadas/removidas em papéis sem exigir re-login; o
    // session do localStorage fica velho até um novo login. Isso sincroniza o
    // contexto sem forçar o usuário a deslogar.
    const token = getToken();
    if (!token) {
      setIsLoading(false);
      return;
    }
    getCurrentUser(token)
      .then((data) => {
        const perms = (data as { permissions?: string[] })?.permissions ?? [];
        setPermissions(perms);
      })
      .catch((err: unknown) => {
        Sentry.captureException(err, { level: "warning", tags: { flow: "permission-refresh" } });
      })
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <PermissionsContext.Provider value={{ permissions, setPermissions, isLoading }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissionsContext = (): PermissionsContextType => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error("usePermissionsContext must be used within a PermissionsProvider");
  }
  return context;
};
